import Highcharts from 'highcharts'
import { Chart as VueChart } from 'highcharts-vue'

import { ref, Ref } from 'vue'
import { BaseSerializedChart } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import vuetify from '@/plugins/vuetify'
import { useSettingsStore } from '@/store/settings'
import { CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES'

export abstract class GenericChartBuilder {
  get allHidden(): boolean {
    return this._allHidden.value
  }
  get allShown(): boolean {
    return this._allShown.value
  }
  get loading(): boolean {
    return this._loading
  }
  set series(value: Highcharts.SeriesOptionsType[]) {
    this._series = value
  }
  get series(): Highcharts.SeriesOptionsType[] | undefined {
    return this._series
  }
  title?: string
  subtitle?: string
  chartEntity?: string
  defaultCollapsed: boolean = false
  protected chartRef?: Ref<typeof VueChart>
  private _loading: boolean = false
  protected options?: Highcharts.Options
  private _series?: Highcharts.SeriesOptionsType[]
  private updateListeners: Array<() => void> = []
  protected dataPointClickListeners: Array<(event: Highcharts.PointerEventObject) => void> = []
  protected abortController: AbortController | undefined
  protected _allHidden = ref(false)
  protected _allShown = ref(false)
  protected paused: boolean = false
  protected seriesBasedHeight: boolean = false
  protected yAxisType?: 'linear' | 'logarithmic'
  private loadCounter: number = 0

  protected plotOptions: Highcharts.PlotOptions = {
    series: {
      point: {
        events: {
          click: (event: Highcharts.PointerEventObject) => {
            this.openDataPointClickDialog(event)
          },
        },
      },
      stickyTracking: false,
      events: {
        hide: () => {
          if (!this.chartRef?.value) return
          this._allShown.value = this.chartRef?.value?.chart.series.every((series: Highcharts.Series) => series.visible)
          this._allHidden.value = this.chartRef?.value?.chart.series.every((series: Highcharts.Series) => !series.visible)
        },
        show: () => {
          if (!this.chartRef?.value) return
          this._allShown.value = this.chartRef?.value?.chart.series.every((series: Highcharts.Series) => series.visible)
          this._allHidden.value = this.chartRef?.value?.chart.series.every((series: Highcharts.Series) => !series.visible)
        },
      },
    },
  }

  protected constructor(serialized: BaseSerializedChart) {
    if (serialized.title) this.title = serialized.title
    if (serialized.subtitle) this.subtitle = serialized.subtitle
    if (serialized.chartEntity) this.chartEntity = serialized.chartEntity
    if (serialized.defaultCollapsed) this.defaultCollapsed = serialized.defaultCollapsed as boolean
    if (serialized.seriesBasedHeight) this.seriesBasedHeight = serialized.seriesBasedHeight
    if (serialized.yAxisType) {
      this.yAxisType = serialized.yAxisType
    }
  }

  abstract openDataPointClickDialog(event: Highcharts.PointerEventObject): void

  attachChartInstance(chartRef: Ref<typeof VueChart>) {
    this.chartRef = chartRef
  }

  getOptions(): Highcharts.Options {
    if (!this.options) {
      this.buildOptions()
    }
    return this.options as Highcharts.Options
  }

  serialize(): BaseSerializedChart {
    const result: BaseSerializedChart = {
      chartType: CHART_TYPES.LINE,
      title: this.title,
      subtitle: this.subtitle,
      chartEntity: this.chartEntity,
      ...(this.yAxisType && { yAxisType: this.yAxisType }),
    }
    if (this.seriesBasedHeight) result.seriesBasedHeight = this.seriesBasedHeight
    return result
  }

  async abortLoading(silent: boolean = false) {
    if (this.abortController) {
      this.abortController.abort()
      await Promise.resolve()
      this._loading = false
      if (!silent) this.triggerUpdate()
    }
    this.abortController = new AbortController()
  }
  async load() {
    this.loadCounter++
    await this.abortLoading(true)
    this._loading = true
    this.triggerUpdate()
    await this.loadData()
    this._loading = false
    this.triggerUpdate()
  }

  protected abstract loadData(): Promise<void>

  abstract buildOptions(): void

  attachUpdateHandler(listener: () => void) {
    this.updateListeners.push(listener)
  }

  detachUpdateHandler(listener: () => void) {
    this.updateListeners.splice(this.updateListeners.indexOf(listener), 1)
  }

  attachDataPointClickHandler(listener: (event: Highcharts.PointerEventObject) => void) {
    this.dataPointClickListeners.push(listener)
  }

  detachDataPointClickHandler(listener: (event: Highcharts.PointerEventObject) => void) {
    this.dataPointClickListeners.splice(this.dataPointClickListeners.indexOf(listener), 1)
  }

  triggerUpdate() {
    this.updateListeners.forEach(listener => listener())
  }

  abstract autoInferChartEntity(serialized?: BaseSerializedChart): string | undefined

  showAll(): void {
    this.chartRef?.value?.chart.series?.forEach((series: Highcharts.Series) => series.setVisible(true, false))
    this.chartRef?.value?.chart.redraw()
  }

  hideAll(): void {
    this.chartRef?.value?.chart.series?.forEach((series: Highcharts.Series) => series.setVisible(false, false))
    this.chartRef?.value?.chart.redraw()
  }

  pauseUpdates(): void {
    this.paused = true
  }

  resumeUpdates(): void {
    this.paused = false
  }

  protected buildYAxisOptions(): Highcharts.YAxisOptions {
    const theme = vuetify.theme.themes.value[useSettingsStore().theme]
    return {
      type: this.yAxisType || 'linear',
      gridLineColor: theme.colors.onSurfaceLowOpacity,
      labels: {
        style: {
          color: theme.colors.onSurface,
        },
      },
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChartCreated(chart: Highcharts.Chart) {}
}
